/**
 * Zustand Auth Store - Supabase Authentication
 *
 * Authenticates via Supabase (email + password).
 * All Kleda kantoor users log in with their Cinco account.
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

// User type for the office app
export interface AppUser {
  id: string;
  email: string;
  displayName: string;
  username: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AppUser | null;
  token: string | null;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  validateToken: () => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
}

/**
 * Map a Supabase user + session to our AppUser type
 */
function mapUser(user: User): AppUser {
  return {
    id: user.id,
    email: user.email || '',
    displayName: user.user_metadata?.display_name || user.email || '',
    username: user.email || '',
  };
}

export const useAuthStore = create<AuthState>()(
  (set) => ({
    isAuthenticated: false,
    isLoading: true, // Start loading until we check the session
    user: null,
    token: null,
    error: null,

    login: async (email: string, password: string) => {
      set({ isLoading: true, error: null });

      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          set({ isLoading: false, error: error.message });
          return false;
        }

        const { user, session } = data;

        set({
          isAuthenticated: true,
          isLoading: false,
          user: mapUser(user),
          token: session.access_token,
          error: null,
        });

        return true;
      } catch (error) {
        console.error('[Auth] Login error:', error);
        set({
          isLoading: false,
          error: 'Kon geen verbinding maken met de server. Probeer het opnieuw.',
        });
        return false;
      }
    },

    validateToken: async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          set({
            isAuthenticated: true,
            isLoading: false,
            user: mapUser(session.user),
            token: session.access_token,
          });
          return true;
        }

        set({ isAuthenticated: false, isLoading: false, user: null, token: null });
        return false;
      } catch {
        set({ isAuthenticated: false, isLoading: false, user: null, token: null });
        return false;
      }
    },

    logout: () => {
      supabase.auth.signOut();
      set({
        isAuthenticated: false,
        user: null,
        token: null,
        error: null,
      });
    },

    clearError: () => set({ error: null }),
  })
);

/**
 * Listen to Supabase auth state changes and keep store in sync.
 */
supabase.auth.onAuthStateChange((_event, session) => {
  if (session?.user) {
    useAuthStore.setState({
      isAuthenticated: true,
      isLoading: false,
      user: mapUser(session.user),
      token: session.access_token,
    });
  } else {
    useAuthStore.setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      token: null,
    });
  }
});

/**
 * Get the current auth token for API requests.
 * Reads from Supabase session (async).
 */
export async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}
